import { Divider, Stack } from '@mui/material';
import type {
  ListInventoryItemSnapshotsResponse,
  InventoryResource,
  RdsNativePitrWindow,
} from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { isNativeBackupResource } from '@/app/(dashboard)/inventory/inventoryResourceUtils';
import { Loader } from '@/components/layout/loading';
import { hasSecurityIssue } from '@/components/security/securityUtils';
import {
  PointsInTimeCalendar,
  type PointInTimeEvent,
} from '@/components/snapshots/pointsInTimeCalendar';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { NativeVaultColor } from '@/theme/colorSchemes';
import { isDateValid } from '@/utils/dateTime';
import { dayjs, DATE_FORMAT, type Dayjs } from '@/utils/dayjs';

import { OtherSnapshotsToggle } from './otherSnapshotsToggle';
import { RestoreSnapshotDrawer } from './restoreSnapshotDrawer';
import { SnapshotsSummary } from './snapshotSummary';

import { ConvertToEonSnapshotsBar } from '../../../../bars/convertToEonSnapshotsBar';

export type MutateSnapshots = (
  data?: (
    current: ListInventoryItemSnapshotsResponse | undefined
  ) => ListInventoryItemSnapshotsResponse | undefined,
  opts?: { revalidate: boolean }
) => void;

const useInitialDate = (
  entity: InventoryResource,
  continuousSnapshots: Partial<RdsNativePitrWindow> | undefined
) => {
  return useMemo(() => {
    const latestSnapshot = isDateValid(entity.latestSnapshotTime)
      ? entity.latestSnapshotTime
      : undefined;
    const continuousLatest = isDateValid(
      continuousSnapshots?.latestRestorableTime
    )
      ? continuousSnapshots?.latestRestorableTime
      : undefined;

    const time =
      latestSnapshot || continuousLatest
        ? (latestSnapshot?.getTime() ?? 0) >= (continuousLatest?.getTime() ?? 0)
          ? latestSnapshot
          : continuousLatest
        : entity.clusterLatestSnapshotTime;

    return (
      isDateValid(time) ? dayjs.utc(time).startOf('day') : dayjs.utc()
    ).toDate();
  }, [entity, continuousSnapshots?.latestRestorableTime]);
};

const computeBigQueryPitrWindow = (
  entity: InventoryResource
): Partial<RdsNativePitrWindow> | undefined => {
  const bqProps = entity.resourceProperties?.gcpBigQuery;

  const timeTravelDays =
    bqProps?.timeTravelWindowDays && bqProps.timeTravelWindowDays > 0
      ? bqProps.timeTravelWindowDays
      : 7;

  const timeTravelStart = dayjs.utc().subtract(timeTravelDays, 'day');

  const datasetCreationTime = bqProps?.creationTime
    ? dayjs.utc(bqProps.creationTime)
    : entity.createdTime
      ? dayjs.utc(entity.createdTime)
      : undefined;

  const earliest = datasetCreationTime?.isAfter(timeTravelStart)
    ? datasetCreationTime
    : timeTravelStart;

  return {
    earliestRestorableTime: earliest.toDate(),
    latestRestorableTime: dayjs.utc().toDate(),
    backupRetentionDays: timeTravelDays,
  };
};

export const EonSnapshotsTab = ({ entity }: { entity: InventoryResource }) => {
  const dal = useDAL();
  const { RdsPitrEnabled, BigQueryNativeRestoreEnabled } = useFeatureFlags();
  const [snapshotDataOpen, setSnapshotDataOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<Dayjs | undefined>();
  const { data: otherSnapshots } = dal.convert.useListDailySnapshots(entity.id);
  const { data: rdsPitrWindow, isLoading: isContinuousSnapshotsLoading } =
    dal.inventory.snapshots.rds.usePitrWindow(entity.id, entity.resourceType);

  const [showOtherSnapshots, setShowOtherSnapshots] = useState(
    isNativeBackupResource(entity)
  );
  const { vaults } = useBackupVaults();

  const bigQueryPitrWindow: Partial<RdsNativePitrWindow> | undefined =
    useMemo(() => {
      if (
        entity.resourceType !== ResourceType.GcpBigQuery ||
        !BigQueryNativeRestoreEnabled
      ) {
        return undefined;
      }

      return computeBigQueryPitrWindow(entity);
    }, [entity, BigQueryNativeRestoreEnabled]);

  const continuousSnapshots = rdsPitrWindow || bigQueryPitrWindow;
  const initialDate = useInitialDate(entity, continuousSnapshots);
  const isInitialDateStillCalculating = isContinuousSnapshotsLoading;

  const [startEndDates, setStartEndDates] = useState<
    | {
        start: Dayjs;
        end: Dayjs;
      }
    | undefined
  >(
    initialDate
      ? {
          start: dayjs.utc(initialDate).startOf('month'),
          end: dayjs.utc(initialDate).endOf('month'),
        }
      : undefined
  );

  const {
    data: eonSnapshots,
    isValidating: isEonSnapshotsValidating,
    mutate: mutateSnapshots,
  } = dal.inventory.snapshots.useList(
    entity.id,
    0,
    3000,
    startEndDates?.start?.toDate(),
    startEndDates?.end?.toDate()
  );

  const snapshots = useMemo(() => {
    return (eonSnapshots?.data || []).filter((snap) => {
      if (isDateValid(snap.pointInTime)) {
        // Include native snapshots (no vault) and vault snapshots
        if (snap.isNative) {
          return true;
        }
        if (snap.vaultId) {
          return vaults.some((v) => v.id === snap.vaultId);
        }
      }
      return false;
    });
  }, [eonSnapshots?.data, vaults]);

  useEffect(() => {
    setSelected(undefined);
    setShowOtherSnapshots(isNativeBackupResource(entity));
    setSnapshotDataOpen(false);
    setStartEndDates(
      initialDate
        ? {
            start: dayjs.utc(initialDate).startOf('month'),
            end: dayjs.utc(initialDate).endOf('month'),
          }
        : undefined
    );
  }, [entity, initialDate]);

  const eonSnapsEvents: PointInTimeEvent[] = snapshots.map((snap) => {
    const vault = vaults.find((v) => v.id === snap.vaultId);

    const date = isDateValid(snap.pointInTime)
      ? snap.pointInTime
      : snap.createdTime;

    const end = dayjs.utc(date).add(1, 'minute');

    return {
      start: date,
      end: end.toDate(),
      vaultName: vault ? vault.name : snap.isNative ? 'AWS Backup Vault' : '--',
      indicatorColor:
        vault?.backgroundColor ?? (snap.isNative ? NativeVaultColor : null),
      status: snap.status,
      accessDeniedItemsCount: snap.accessDeniedItemsCount,
      hasSecurityIssue: hasSecurityIssue(snap),
      supportPiTR: snap.supportPiTR,
      protectedFromDeletion: snap.protectedFromDeletion,
      onHold: snap.onHold,
    };
  });

  const otherSnapsEvents: PointInTimeEvent[] = (
    showOtherSnapshots && otherSnapshots?.dailySnapshots
      ? otherSnapshots.dailySnapshots
      : []
  )
    .filter((x) => isDateValid(x.startedAt))
    .map((snap) => {
      const start = snap.startedAt!;
      const end = dayjs.utc(start).add(1, 'minute');

      return {
        start,
        end: end.toDate(),
        isProviderSnapshot: true,
        indicatorColor: null,
      };
    });

  const hasProviderSnapsInDate = otherSnapsEvents.some((snap) =>
    selected?.isSame(dayjs.utc(snap.start), 'day')
  );

  const seen = new Set();
  const otherSnapsEventsDistinct = otherSnapsEvents.filter((snap) => {
    const dateStr = dayjs.utc(snap.start).format(DATE_FORMAT);
    if (!seen.has(dateStr)) {
      seen.add(dateStr);
      return true;
    }

    return false;
  });

  const isBigQueryPitrAvailable =
    BigQueryNativeRestoreEnabled &&
    entity.resourceType === ResourceType.GcpBigQuery;

  const continuousSnapsEvents: PointInTimeEvent[] = (() => {
    if (
      (!RdsPitrEnabled && !isBigQueryPitrAvailable) ||
      !continuousSnapshots?.earliestRestorableTime ||
      !continuousSnapshots?.latestRestorableTime
    ) {
      return [];
    }

    const earliest = dayjs
      .utc(continuousSnapshots.earliestRestorableTime)
      .startOf('day');
    const latest = dayjs
      .utc(continuousSnapshots.latestRestorableTime)
      .startOf('day');
    const numOfDays = latest.diff(earliest, 'day') + 1;

    return Array.from({ length: numOfDays }, (_, i) => {
      const start = earliest.add(i, 'day').toDate();
      const end = dayjs.utc(start).add(1, 'minute').toDate();

      return {
        start,
        end,
        indicatorColor: NativeVaultColor,
        supportPiTR: true,
      };
    });
  })();

  const events: PointInTimeEvent[] = [
    ...eonSnapsEvents.filter((x) => !x.supportPiTR),
    ...eonSnapsEvents.filter((x) => x.supportPiTR),
    ...otherSnapsEventsDistinct,
    ...continuousSnapsEvents,
  ];

  if (isInitialDateStillCalculating) {
    return <Loader />;
  }

  return (
    <Stack
      direction='column'
      justifyContent='space-between'
      height='100%'
      sx={{ margin: '0 auto' }}
      flexGrow='1'
      width='100%'
      overflow='hidden'
    >
      <Stack flexGrow='1' overflow='auto'>
        <OtherSnapshotsToggle
          resource={entity}
          showOtherSnapshots={showOtherSnapshots}
          setShowOtherSnapshots={(value) => {
            setSelected(undefined);
            setShowOtherSnapshots(value);
          }}
        />
        <SnapshotsSummary entity={entity} />
        <Divider />
        <PointsInTimeCalendar
          isLoading={isEonSnapshotsValidating}
          initialDate={initialDate}
          onDateRangeChanged={(start, end) => {
            setStartEndDates({ start, end });
            setSelected(undefined);
          }}
          events={events}
          onDateSelected={(selected) => {
            if (selected) {
              setSnapshotDataOpen(true);
            }
            setSelected(selected);
          }}
          selectedDate={selected}
        />
      </Stack>
      {snapshotDataOpen && selected ? (
        <RestoreSnapshotDrawer
          vaults={vaults}
          eonSnapshots={snapshots}
          hasProviderSnapsInDate={hasProviderSnapsInDate}
          showOtherSnapshots={showOtherSnapshots}
          continuousSnapshots={continuousSnapshots}
          selectedDate={selected?.toDate()}
          entity={entity}
          mutateSnapshots={() => void mutateSnapshots()}
          onClose={() => {
            setSelected(undefined);
            setSnapshotDataOpen(false);
          }}
        />
      ) : (
        <ConvertToEonSnapshotsBar
          numOfOtherSnapshots={entity.snapshotStorage.nonEonSnapshotCount || 0}
          cloudProvider={entity.cloudProvider}
          resource={entity}
        />
      )}
    </Stack>
  );
};
